import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FolderTree, Package, Search, ArrowRight, ArrowLeft } from "lucide-react";
import BulkSelectionBar, { useBulkSelection, SelectAllHeader, RowCheckbox } from "@/components/BulkSelectionBar";
import CategoryIconPicker, { CategoryIcon } from "@/components/CategoryIconPicker";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"];
const PRESETS = [
  { name: "مشروبات", color: "#0EA5E9" },
  { name: "غذائيات", color: "#F59E0B" },
  { name: "منظفات", color: "#10B981" },
  { name: "ألبان وأجبان", color: "#EAB308" },
  { name: "معلبات", color: "#EF4444" },
  { name: "أدوات منزلية", color: "#8B5CF6" },
  { name: "خضار وفواكه", color: "#22C55E" },
  { name: "لحوم", color: "#DC2626" },
  { name: "حلويات", color: "#EC4899" },
  { name: "أدوية OTC", color: "#3B82F6" },
];

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#3B82F6", icon: "Package", parent_id: "", image_url: "" });
  const [formImage, setFormImage] = useState({ preview: "", b64: "", type: "" });

  // Manage-products dialog state
  const [manageCat, setManageCat] = useState(null);
  const [inside, setInside] = useState([]);
  const [outside, setOutside] = useState([]);
  const [q, setQ] = useState("");
  const [dragOverId, setDragOverId] = useState(null);
  const bulkIn = useBulkSelection(inside.map(x => x.id));
  const bulkOut = useBulkSelection(outside.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.barcode||"").includes(q)).map(x => x.id));

  const load = async () => {
    try { const { data } = await api.get("/categories"); setItems(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", color: "#3B82F6", icon: "Package", parent_id: "", image_url: "" }); setFormImage({ preview: "", b64: "", type: "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color || "#3B82F6", icon: c.icon || "Package", parent_id: c.parent_id || "", image_url: c.image_url || "" }); setFormImage({ preview: c.image_url || "", b64: "", type: "" }); setOpen(true); };
  const pickCategoryImage = async (file) => { if (!file) return; const b64 = await new Promise(resolve => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(",")[1] || ""); reader.readAsDataURL(file); }); setFormImage({ preview: URL.createObjectURL(file), b64, type: file.type || "image/png" }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("ادخل اسم المجموعة");
    try {
      let saved;
      if (editing) { const response = await api.put(`/categories/${editing.id}`, form); saved = response.data; }
      else { const response = await api.post("/categories", form); saved = response.data; }
      if (formImage.b64 && saved?.id) { const response = await api.post(`/categories/${saved.id}/image`, { content_type: formImage.type || "image/png", data_b64: formImage.b64 }); setFormImage({ preview: response.data?.image_url || "", b64: "", type: "" }); }
      toast.success("تم الحفظ"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (c) => {
    if (!confirm(`حذف مجموعة «${c.name}»؟ الأصناف ستحتفظ بالفئة كنص لكن بدون ربط.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addPreset = async (p) => {
    try { await api.post("/categories", { name: p.name, color: p.color }); toast.success(`تمت إضافة ${p.name}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  // ---- Manage products in category ----
  const openManage = async (c) => {
    setManageCat(c);
    setQ("");
    bulkIn.clear(); bulkOut.clear();
    try {
      const { data } = await api.get(`/categories/${c.id}/products`);
      setInside(data.inside || []);
      setOutside(data.outside || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const refreshManage = async () => {
    if (!manageCat) return;
    try {
      const { data } = await api.get(`/categories/${manageCat.id}/products`);
      setInside(data.inside || []);
      setOutside(data.outside || []);
      bulkIn.clear(); bulkOut.clear();
      load(); // refresh counts
    } catch (e) { toast.error(formatErr(e)); }
  };
  const attachSelected = async () => {
    const ids = bulkOut.selectedIds;
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/categories/${manageCat.id}/attach-products`, { ids });
      toast.success(`تمت إضافة ${data.updated} صنف إلى «${manageCat.name}»`);
      refreshManage();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const detachSelected = async () => {
    const ids = bulkIn.selectedIds;
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/categories/${manageCat.id}/detach-products`, { ids });
      toast.success(`تم استبعاد ${data.updated} صنف من «${manageCat.name}»`);
      refreshManage();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---- Drag & Drop between category cards ----
  const onDragStartRow = (e, productId) => {
    // If the product isn't in the current bulk selection, treat this row as a single drag
    const ids = bulkIn.isSelected(productId) && bulkIn.count > 0
      ? bulkIn.selectedIds
      : [productId];
    e.dataTransfer.setData("application/x-muhasabaty-products", JSON.stringify({
      product_ids: ids,
      from_category_id: manageCat?.id,
    }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onCardDragOver = (e, catId) => {
    // Only accept product drops
    if (!e.dataTransfer.types.includes("application/x-muhasabaty-products")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(catId);
  };
  const onCardDragLeave = () => setDragOverId(null);
  const onCardDrop = async (e, targetCat) => {
    e.preventDefault();
    setDragOverId(null);
    const raw = e.dataTransfer.getData("application/x-muhasabaty-products");
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    const ids = payload.product_ids || [];
    const fromId = payload.from_category_id;
    if (!ids.length) return;
    if (fromId === targetCat.id) return toast.info("الأصناف بالفعل في هذه المجموعة");
    try {
      // 1) attach to target category
      await api.post(`/categories/${targetCat.id}/attach-products`, { ids });
      // 2) detach from source (only if we know it) — this makes it feel like a "move"
      if (fromId) {
        await api.post(`/categories/${fromId}/detach-products`, { ids });
      }
      toast.success(`تم نقل ${ids.length} صنف إلى «${targetCat.name}»`);
      // If manage dialog is still open on the source, refresh it
      if (manageCat && manageCat.id === fromId) refreshManage();
      else load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filteredOutside = outside.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.barcode||"").includes(q));
  const treeItems = (() => {
    const out = [];
    const walk = (parentId, level) => items.filter(c => (c.parent_id || "") === (parentId || "")).forEach(c => { out.push({ c, level }); walk(c.id, level + 1); });
    walk("", 0);
    items.filter(c => c.parent_id && !items.some(p => p.id === c.parent_id)).forEach(c => out.push({ c, level: 0 }));
    return out;
  })();

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><FolderTree className="w-6 h-6 text-primary" /> مجموعات الأصناف</h1>
          <p className="text-sm text-muted-foreground mt-1">صنّف أصنافك في مجموعات (مشروبات، غذائيات، منظفات...) لتصفية سهلة داخل الفوترة</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-category-btn"><Plus className="w-4 h-4" /> مجموعة جديدة</Button>
      </div>

      {items.length === 0 && (
        <Card className="p-6 border-dashed">
          <div className="text-sm text-muted-foreground mb-3">ابدأ بإضافة مجموعات جاهزة بضغطة واحدة:</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(p => (
              <button key={p.name} onClick={()=>addPreset(p)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border hover:border-primary hover:bg-accent transition-all text-sm" data-testid={`preset-${p.name}`}>
                <span className="w-3 h-3 rounded-full" style={{background: p.color}} />
                {p.name}
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="category-tree">
        {treeItems.map(({ c, level }) => (
          <Card
            key={c.id}
            className={`p-4 hover:shadow-md transition-all cursor-pointer group ${dragOverId === c.id ? "ring-2 ring-primary ring-offset-2 scale-105" : ""}`}
            style={{ marginInlineStart: `${Math.min(level, 3) * 12}px` }}
            data-testid={`cat-${c.id}`}
            onClick={() => openManage(c)}
            onDragOver={(e) => onCardDragOver(e, c.id)}
            onDragLeave={onCardDragLeave}
            onDrop={(e) => onCardDrop(e, c)}
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md grid place-items-center text-white transition-transform overflow-hidden" style={{background: c.color || "#3B82F6"}}>
                {c.image_url ? <img src={c.image_url} alt="" className="w-full h-full object-cover" /> : <CategoryIcon name={c.icon || "Package"} size={20} />}
              </div>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button size="icon" variant="ghost" className="w-7 h-7" onClick={()=>openEdit(c)} data-testid={`edit-cat-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="w-7 h-7 text-destructive" onClick={()=>del(c)} data-testid={`del-cat-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="mt-3 font-heading font-bold flex items-center gap-1">{level > 0 && <span className="text-primary text-xs">└─</span>}{c.name}</div>
            {level > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">قسم فرعي</div>}
            <div className="flex items-center justify-between mt-1">
              <div className="text-xs text-muted-foreground">{c.products_count || 0} صنف</div>
              <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">إدارة الأصناف →</div>
            </div>
            {dragOverId === c.id && (
              <div className="mt-2 text-[10px] text-primary font-bold text-center animate-pulse">أفلت هنا للنقل ⬇</div>
            )}
          </Card>
        ))}
      </div>

      {/* Category edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "تعديل مجموعة" : "مجموعة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>اسم المجموعة</Label><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="مثال: مشروبات" data-testid="cat-name-input" /></div>
            <div><Label>صورة المجموعة للمتجر</Label><div className="flex items-center gap-3 mt-2"><div className="w-20 h-16 rounded-lg border bg-muted overflow-hidden grid place-items-center">{formImage.preview ? <img src={formImage.preview} alt="معاينة المجموعة" className="w-full h-full object-cover" /> : <Package className="text-muted-foreground" />}</div><div><input type="file" accept="image/*" onChange={e=>pickCategoryImage(e.target.files?.[0])} className="text-xs" data-testid="category-image-input" /><p className="text-[10px] text-muted-foreground mt-1">حد أقصى 1MB. ستظهر الصورة في متجر الشركة.</p></div></div></div>
            <div><Label>القسم الرئيسي (اختياري)</Label><select className="w-full h-10 border rounded bg-background px-2 text-sm" value={form.parent_id || ""} onChange={e=>setForm({...form, parent_id: e.target.value})} data-testid="cat-parent-select"><option value="">قسم رئيسي</option>{items.filter(c => c.id !== editing?.id).map(c => <option key={c.id} value={c.id}>{c.parent_id ? `↳ ${c.name}` : c.name}</option>)}</select><div className="text-[10px] text-muted-foreground mt-1">مثال: الشركة ← المشروبات ← العصائر</div></div>
            <div>
              <Label>اللون</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={()=>setForm({...form, color: c})} className={`w-8 h-8 rounded-md border-2 transition-all ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{background: c}} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <div>
              <Label>الأيقونة</Label>
              <div className="flex items-center gap-3 mt-2 mb-2">
                <div className="w-12 h-12 rounded-md grid place-items-center text-white shrink-0" style={{background: form.color}}>
                  <CategoryIcon name={form.icon} size={24} />
                </div>
                <div className="text-xs text-muted-foreground">اختر أيقونة تعبّر عن المجموعة — يستطيع الكاشير التعرف عليها بسرعة أثناء البيع</div>
              </div>
              <CategoryIconPicker value={form.icon} onChange={(icon) => setForm({...form, icon})} color={form.color} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-cat-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage products in category dialog — non-modal so users can drag rows onto category cards behind */}
      <Dialog open={!!manageCat} onOpenChange={(o) => !o && setManageCat(null)} modal={false}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl border-2 border-primary/20" data-testid="manage-cat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-md" style={{background: manageCat?.color || "#3B82F6"}} />
              إدارة أصناف «{manageCat?.name}»
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="inside" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="inside" data-testid="tab-inside">داخل المجموعة ({inside.length})</TabsTrigger>
              <TabsTrigger value="outside" data-testid="tab-outside">إضافة أصناف ({outside.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="inside" className="flex-1 overflow-hidden flex flex-col mt-3">
              <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 rounded-md px-3 py-1.5 mb-2 flex items-center gap-2" data-testid="drag-hint">
                <span className="text-blue-600">💡</span>
                <span>يمكنك <b>سحب أي صنف</b> وإفلاته على مجموعة أخرى في الشبكة خلف هذا الحوار للنقل السريع</span>
              </div>
              <BulkSelectionBar bulk={bulkIn} label="صنف">
                <Button size="sm" variant="outline" onClick={detachSelected} className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="detach-selected-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> استبعاد من المجموعة
                </Button>
              </BulkSelectionBar>
              <div className="flex-1 overflow-y-auto border rounded-md">
                {inside.length === 0 && <div className="text-center text-muted-foreground py-8 text-sm">لا توجد أصناف في هذه المجموعة بعد. انتقل إلى «إضافة أصناف» للبدء.</div>}
                {inside.map(p => {
                  const tags = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);
                  return (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 p-3 hover:bg-accent border-b cursor-grab active:cursor-grabbing"
                      data-testid={`inside-row-${p.id}`}
                      draggable
                      onDragStart={(e) => onDragStartRow(e, p.id)}
                    >
                      <span className="text-muted-foreground select-none" title="اسحب لنقله لمجموعة أخرى">⋮⋮</span>
                      <RowCheckbox bulk={bulkIn} id={p.id} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{p.barcode || "—"} · {p.sale_price?.toLocaleString?.("ar-EG")} {p.currency}</div>
                        {tags.length > 1 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`inside-tags-${p.id}`}>
                            <span className="text-[10px] text-muted-foreground">أيضاً في:</span>
                            {tags.filter(t => t !== manageCat?.name).map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="outside" className="flex-1 overflow-hidden flex flex-col mt-3">
              <div className="relative mb-2">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="بحث عن صنف بالاسم أو الباركود..." value={q} onChange={(e)=>setQ(e.target.value)} className="ps-9" data-testid="outside-search-input" />
              </div>
              <BulkSelectionBar bulk={bulkOut} label="صنف">
                <Button size="sm" onClick={attachSelected} className="gap-1" data-testid="attach-selected-btn">
                  <ArrowRight className="w-3.5 h-3.5" /> أضف للمجموعة
                </Button>
              </BulkSelectionBar>
              <div className="flex-1 overflow-y-auto border rounded-md">
                {filteredOutside.length === 0 && <div className="text-center text-muted-foreground py-8 text-sm">لا توجد أصناف {q ? "مطابقة للبحث" : "خارج هذه المجموعة"}.</div>}
                {filteredOutside.map(p => {
                  const tags = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-3 hover:bg-accent border-b" data-testid={`outside-row-${p.id}`}>
                      <RowCheckbox bulk={bulkOut} id={p.id} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{p.barcode || "—"} · {p.sale_price?.toLocaleString?.("ar-EG")} {p.currency}</div>
                        {tags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            <span className="text-[10px] text-amber-600">حالياً في:</span>
                            {tags.map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
